import React, { useMemo, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowUpRight, ArrowDownRight, Search, Download, CreditCard, Building, Receipt, FileText, CheckCircle2, Calendar, Smartphone, Hash, Wallet as WalletIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useAppContext, Transaction, User } from '../contexts/AppContext';
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { BillvastLogo } from '../components/ui/BillvastLogo';
import { toast } from 'sonner';
const COLORS = ['#73C883', '#FFB347', '#3b82f6', '#8b5cf6', '#f43f5e'];
// Fixed authorized signatory
const SIGNATORY_NAME = 'Emmanuel Alonge';
// SVG stamp + signature HTML for PDFs
const getStampAndSignatureHTML = () => {
  const stampDate = new Date().toLocaleDateString('en-NG', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return ['<div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:40px;padding:0 20px;">', '  <div style="text-align:center;">', '    <svg width="150" height="150" viewBox="0 0 150 150">', '      <defs>', '        <path id="upperArc" d="M 20,75 A 55,55 0 0,1 130,75" fill="none"/>', '        <path id="lowerArc" d="M 130,75 A 55,55 0 0,1 20,75" fill="none"/>', '      </defs>', '      <circle cx="75" cy="75" r="70" fill="none" stroke="#006633" stroke-width="3.5" opacity="0.75"/>', '      <circle cx="75" cy="75" r="64" fill="none" stroke="#006633" stroke-width="1.5" opacity="0.5"/>', '      <circle cx="75" cy="75" r="61" fill="none" stroke="#006633" stroke-width="0.5" opacity="0.35"/>', '      <text font-size="8.5" font-weight="700" fill="#006633" letter-spacing="4" opacity="0.85">', '        <textPath href="#upperArc" startOffset="50%" text-anchor="middle">BILLVAST FINTECH LTD</textPath>', '      </text>', '      <text font-size="7" font-weight="600" fill="#006633" letter-spacing="3" opacity="0.7">', '        <textPath href="#lowerArc" startOffset="50%" text-anchor="middle">LAGOS &#8226; NIGERIA</textPath>', '      </text>', '      <text x="75" y="55" text-anchor="middle" font-size="7" fill="#006633" opacity="0.5" letter-spacing="1">RC: 1847293</text>', '      <line x1="45" y1="60" x2="105" y2="60" stroke="#006633" stroke-width="0.5" opacity="0.3"/>', '      <text x="75" y="76" text-anchor="middle" font-size="14" font-weight="800" fill="#006633">&#10003; VERIFIED</text>', '      <line x1="45" y1="82" x2="105" y2="82" stroke="#006633" stroke-width="0.5" opacity="0.3"/>', '      <text x="75" y="95" text-anchor="middle" font-size="6.5" fill="#006633" opacity="0.6">' + stampDate + '</text>', '      <circle cx="30" cy="75" r="1.5" fill="#006633" opacity="0.5"/>', '      <circle cx="120" cy="75" r="1.5" fill="#006633" opacity="0.5"/>', '      <polygon points="75,22 76.5,26 80,26 77,28.5 78.5,32 75,30 71.5,32 73,28.5 70,26 73.5,26" fill="#006633" opacity="0.5"/>', '      <polygon points="75,118 76.5,122 80,122 77,124.5 78.5,128 75,126 71.5,128 73,124.5 70,122 73.5,122" fill="#006633" opacity="0.5"/>', '    </svg>', '  </div>', '  <div style="text-align:center;">', '    <svg width="180" height="65" viewBox="0 0 180 65">', '      <path d="M8 42 C15 18, 28 8, 42 22 S58 42, 68 20 C74 10, 82 8, 90 28 S102 44, 112 22 C118 10, 128 14, 138 30 C144 38, 152 36, 162 28" fill="none" stroke="#1A2E1A" stroke-width="2.2" stroke-linecap="round" opacity="0.75"/>', '      <path d="M22 48 C35 40, 55 36, 75 42 S100 50, 125 40 C135 36, 145 38, 155 42" fill="none" stroke="#1A2E1A" stroke-width="1" stroke-linecap="round" opacity="0.35"/>', '      <path d="M60 50 C70 46, 80 44, 95 48" fill="none" stroke="#1A2E1A" stroke-width="0.8" stroke-linecap="round" opacity="0.25"/>', '    </svg>', '    <div style="font-size:13px;font-weight:700;color:#1A2E1A;margin-top:4px;letter-spacing:0.5px;">' + SIGNATORY_NAME + '</div>', '    <div style="font-size:10px;color:#6B7280;margin-top:2px;">Authorized Signatory</div>', '    <div style="font-size:9px;color:#9CA3AF;margin-top:1px;">Chief Executive Officer</div>', '    <div style="border-top:1.5px solid #d1d5db;width:160px;margin:6px auto 0;"></div>', '  </div>', '</div>'].join('\n');
};
// --- Helper Functions for Generation ---
const generateReceiptPDF = (tx: Transaction) => {
  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Please allow popups to generate PDF');
    return;
  }
  const amountColor = tx.amount > 0 ? '#006633' : '#1A2E1A';
  const amountFormatted = (tx.amount > 0 ? '+' : '') + formatCurrency(tx.amount);
  const stampAndSig = getStampAndSignatureHTML();
  const html = ['<!DOCTYPE html>', '<html>', '<head>', '  <title>Receipt - ' + tx.reference + '</title>', '  <style>{`', '    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");', '    * { box-sizing: border-box; margin: 0; padding: 0; }', '    body { font-family: "Inter", sans-serif; padding: 40px; color: #1A2E1A; max-width: 600px; margin: 0 auto; background: #fff; }', '    .header { text-align: center; margin-bottom: 30px; }', '    .logo { max-width: 80px; margin-bottom: 16px; }', '    .title { font-size: 20px; font-weight: 600; color: #1A2E1A; text-transform: uppercase; letter-spacing: 1px; }', '    .divider { border-top: 2px dashed #e5e7eb; margin: 24px 0; }', '    .amount-container { text-align: center; margin: 30px 0; }', '    .amount-label { color: #6B7280; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }', '    .amount { font-size: 40px; font-weight: 700; color: ' + amountColor + '; }', '    .row { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 15px; }', '    .label { color: #6B7280; }', '    .value { font-weight: 600; text-align: right; max-width: 60%; }', '    .status { color: #059669; font-weight: 600; display: flex; align-items: center; gap: 6px; justify-content: flex-end; }', '    .footer { text-align: center; margin-top: 30px; color: #6B7280; font-size: 13px; line-height: 1.6; }', '    @media print { body { padding: 20px; } * { -webkit-print-color-adjust: exact; } }', '`}</style>', '</head>', '<body>', '  <div class="header">', '    <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" class="logo" alt="Billvast" />', '    <h1 class="title">Transaction Receipt</h1>', '  </div>', '  <div class="amount-container">', '    <div class="amount-label">Amount</div>', '    <div class="amount">' + amountFormatted + '</div>', '  </div>', '  <div class="divider"></div>', '  <div class="row"><span class="label">Transaction Type</span><span class="value">' + tx.type + '</span></div>', '  <div class="row"><span class="label">Description</span><span class="value">' + tx.title + '</span></div>', '  <div class="row"><span class="label">Date &amp; Time</span><span class="value">' + formatDateTime(tx.date) + '</span></div>', '  <div class="row"><span class="label">Reference ID</span><span class="value" style="font-family:monospace">' + tx.reference + '</span></div>', '  <div class="row"><span class="label">Status</span><span class="value status">&#10003; ' + tx.status + '</span></div>', '  <div class="divider"></div>', stampAndSig, '  <div class="footer">', '    <strong>Thank you for using Billvast.</strong><br/>', '    This is a computer-generated receipt.<br/>', '    If you have any questions, please contact support@billvast.com', '  </div>', '  <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }<\/script>', '</body>', '</html>'].join('\n');
  win.document.write(html);
  win.document.close();
};
const generateReceiptPNG = (tx: Transaction) => {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 950;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 600, 950);
  // Top accent bar
  ctx.fillStyle = '#73C883';
  ctx.fillRect(0, 0, 600, 6);
  // Logo (loaded as image)
  const logoImg = new Image();
  logoImg.crossOrigin = 'anonymous';
  logoImg.src = 'https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png';
  logoImg.onload = () => {
    ctx.drawImage(logoImg, 260, 25, 80, 80);
    drawReceiptContent();
  };
  logoImg.onerror = () => {
    drawReceiptContent();
  };
  const drawReceiptContent = () => {
    ctx.fillStyle = '#1A2E1A';
    ctx.font = '600 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('TRANSACTION RECEIPT', 300, 125);
    // Amount section
    ctx.fillStyle = '#6B7280';
    ctx.font = '13px Arial';
    ctx.fillText('AMOUNT', 300, 160);
    ctx.fillStyle = tx.amount > 0 ? '#006633' : '#1A2E1A';
    ctx.font = 'bold 44px Arial';
    ctx.fillText((tx.amount > 0 ? '+' : '') + formatCurrency(tx.amount), 300, 215);
    // Dashed divider helper
    const drawDivider = (y: number) => {
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.moveTo(50, y);
      ctx.lineTo(550, y);
      ctx.stroke();
      ctx.setLineDash([]);
    };
    drawDivider(260);
    // Details rows
    ctx.textAlign = 'left';
    const details = [{
      label: 'Transaction Type',
      value: tx.type
    }, {
      label: 'Description',
      value: tx.title
    }, {
      label: 'Date & Time',
      value: formatDateTime(tx.date)
    }, {
      label: 'Reference ID',
      value: tx.reference
    }, {
      label: 'Status',
      value: '\u2713 ' + tx.status
    }];
    const startY = 310;
    const lineHeight = 52;
    details.forEach((item, index) => {
      const y = startY + index * lineHeight;
      ctx.fillStyle = '#6B7280';
      ctx.font = '14px Arial';
      ctx.fillText(item.label, 50, y);
      ctx.fillStyle = item.label === 'Status' ? '#059669' : '#1A2E1A';
      ctx.font = item.label === 'Reference ID' ? '14px monospace' : 'bold 14px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(item.value, 550, y);
      ctx.textAlign = 'left';
    });
    drawDivider(580);
    // --- Stamp (circular seal - authentic) ---
    const stampX = 130;
    const stampY = 680;
    const stampR = 65;
    const stampDate = new Date().toLocaleDateString('en-NG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    ctx.save();
    // Outer circle
    ctx.strokeStyle = '#006633';
    ctx.lineWidth = 3.5;
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    ctx.arc(stampX, stampY, stampR, 0, Math.PI * 2);
    ctx.stroke();
    // Inner double circle
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(stampX, stampY, stampR - 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.arc(stampX, stampY, stampR - 10, 0, Math.PI * 2);
    ctx.stroke();
    // Curved text around top - "BILLVAST FINTECH LTD"
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = '#006633';
    ctx.font = 'bold 8px Arial';
    ctx.textAlign = 'center';
    const topText = 'BILLVAST FINTECH LTD';
    const topRadius = stampR - 16;
    const topAngleStart = -Math.PI * 0.82;
    const topAngleSpan = Math.PI * 0.64;
    for (let i = 0; i < topText.length; i++) {
      const angle = topAngleStart + i / (topText.length - 1) * topAngleSpan;
      const cx = stampX + topRadius * Math.cos(angle);
      const cy = stampY + topRadius * Math.sin(angle);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle + Math.PI / 2);
      ctx.fillText(topText[i], 0, 0);
      ctx.restore();
    }
    // Curved text around bottom - "LAGOS • NIGERIA"
    ctx.globalAlpha = 0.65;
    ctx.font = 'bold 7px Arial';
    const bottomText = 'LAGOS \u2022 NIGERIA';
    const bottomRadius = stampR - 16;
    const bottomAngleStart = Math.PI * 0.18;
    const bottomAngleSpan = Math.PI * 0.64;
    for (let i = 0; i < bottomText.length; i++) {
      const angle = bottomAngleStart + i / (bottomText.length - 1) * bottomAngleSpan;
      const cx = stampX + bottomRadius * Math.cos(angle);
      const cy = stampY + bottomRadius * Math.sin(angle);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle - Math.PI / 2);
      ctx.fillText(bottomText[i], 0, 0);
      ctx.restore();
    }
    // Registration number
    ctx.globalAlpha = 0.5;
    ctx.font = '7px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('RC: 1847293', stampX, stampY - 20);
    // Divider lines
    ctx.strokeStyle = '#006633';
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.moveTo(stampX - 30, stampY - 14);
    ctx.lineTo(stampX + 30, stampY - 14);
    ctx.stroke();
    // VERIFIED text
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = '#006633';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('\u2713 VERIFIED', stampX, stampY + 2);
    // Divider below verified
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.moveTo(stampX - 30, stampY + 8);
    ctx.lineTo(stampX + 30, stampY + 8);
    ctx.stroke();
    // Date
    ctx.globalAlpha = 0.55;
    ctx.font = '7px Arial';
    ctx.fillText(stampDate, stampX, stampY + 22);
    // Star decorations
    const drawStar = (sx: number, sy: number, size: number) => {
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = '#006633';
      ctx.beginPath();
      for (let j = 0; j < 5; j++) {
        const a = j * 4 * Math.PI / 5 - Math.PI / 2;
        const r = j % 2 === 0 ? size : size * 0.4;
        const method = j === 0 ? 'moveTo' : 'lineTo';
        ctx[method](sx + r * Math.cos(a), sy + r * Math.sin(a));
      }
      ctx.closePath();
      ctx.fill();
    };
    drawStar(stampX, stampY - stampR + 12, 5);
    drawStar(stampX, stampY + stampR - 12, 5);
    // Side dots
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(stampX - stampR + 14, stampY, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(stampX + stampR - 14, stampY, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    // --- Signature ---
    const sigX = 430;
    const sigY = 660;
    ctx.save();
    ctx.strokeStyle = '#1A2E1A';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    ctx.moveTo(sigX - 65, sigY + 18);
    ctx.bezierCurveTo(sigX - 45, sigY - 12, sigX - 25, sigY - 18, sigX - 5, sigY + 8);
    ctx.bezierCurveTo(sigX + 10, sigY + 22, sigX + 25, sigY - 8, sigX + 40, sigY - 2);
    ctx.bezierCurveTo(sigX + 52, sigY + 4, sigX + 62, sigY + 18, sigX + 75, sigY + 8);
    ctx.bezierCurveTo(sigX + 82, sigY + 2, sigX + 90, sigY - 4, sigX + 95, sigY + 6);
    ctx.stroke();
    // Underline flourish
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sigX - 45, sigY + 28);
    ctx.bezierCurveTo(sigX - 10, sigY + 22, sigX + 25, sigY + 20, sigX + 70, sigY + 26);
    ctx.stroke();
    // Second flourish
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(sigX - 5, sigY + 34);
    ctx.bezierCurveTo(sigX + 15, sigY + 30, sigX + 35, sigY + 28, sigX + 55, sigY + 32);
    ctx.stroke();
    ctx.restore();
    // Signature name
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1A2E1A';
    ctx.font = 'bold 13px Arial';
    ctx.fillText(SIGNATORY_NAME, sigX + 15, sigY + 52);
    ctx.fillStyle = '#6B7280';
    ctx.font = '10px Arial';
    ctx.fillText('Authorized Signatory', sigX + 15, sigY + 67);
    ctx.font = '9px Arial';
    ctx.fillStyle = '#9CA3AF';
    ctx.fillText('Chief Executive Officer', sigX + 15, sigY + 80);
    // Signature line
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(sigX - 55, sigY + 88);
    ctx.lineTo(sigX + 85, sigY + 88);
    ctx.stroke();
    // Footer
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1A2E1A';
    ctx.font = 'bold 13px Arial';
    ctx.fillText('Thank you for using Billvast.', 300, 820);
    ctx.fillStyle = '#6B7280';
    ctx.font = '12px Arial';
    ctx.fillText('This is a computer-generated receipt.', 300, 842);
    ctx.fillText('Questions? Contact support@billvast.com', 300, 862);
    // Bottom accent bar
    ctx.fillStyle = '#73C883';
    ctx.fillRect(0, 944, 600, 6);
    // Trigger download
    const link = document.createElement('a');
    link.download = 'Billvast-Receipt-' + tx.reference + '.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }; // close drawReceiptContent
};
const generateStatementPDF = (allTransactions: Transaction[], user: User, dateRangeDays: number, periodText: string) => {
  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Please allow popups to generate PDF');
    return;
  }
  const now = new Date();
  let startDate = new Date(0);
  if (dateRangeDays > 0) {
    startDate = new Date();
    startDate.setDate(now.getDate() - dateRangeDays);
  }
  const periodTxs = allTransactions.filter(tx => new Date(tx.date) >= startDate);
  const sortedTxs = [...periodTxs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const periodNetChange = periodTxs.reduce((sum, tx) => sum + tx.amount, 0);
  const openingBalance = user.walletBalance - periodNetChange;
  const totalCredits = periodTxs.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalDebits = Math.abs(periodTxs.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0));
  let runningBalance = openingBalance;
  const rowsHtml = sortedTxs.map(tx => {
    runningBalance += tx.amount;
    const debit = tx.amount < 0 ? formatCurrency(Math.abs(tx.amount)) : '-';
    const credit = tx.amount > 0 ? formatCurrency(tx.amount) : '-';
    const debitStyle = tx.amount < 0 ? 'color:#dc2626;' : '';
    const creditStyle = tx.amount > 0 ? 'color:#059669;' : '';
    return ['<tr>', '  <td>' + formatDate(tx.date) + '</td>', '  <td><div style="font-weight:500;color:#1A2E1A">' + tx.title + '</div>', '      <div style="font-size:11px;color:#6B7280">Ref: ' + tx.reference + '</div></td>', '  <td style="text-align:right;' + debitStyle + '">' + debit + '</td>', '  <td style="text-align:right;' + creditStyle + '">' + credit + '</td>', '  <td style="text-align:right;font-weight:600">' + formatCurrency(runningBalance) + '</td>', '</tr>'].join('');
  }).join('');
  const noRowsHtml = '<tr><td colspan="5" style="text-align:center;padding:30px;color:#6B7280">No transactions found for this period.</td></tr>';
  const fromDate = dateRangeDays === 0 ? 'Account Opening' : formatDate(startDate);
  const stampAndSig = getStampAndSignatureHTML();
  const html = ['<!DOCTYPE html>', '<html>', '<head>', '  <title>Account Statement - ' + user.name + '</title>', '  <style>{`', '    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");', '    * { box-sizing: border-box; margin: 0; padding: 0; }', '    body { font-family: "Inter", sans-serif; padding: 40px; color: #1A2E1A; max-width: 900px; margin: 0 auto; background: #fff; }', '    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 2px solid #006633; padding-bottom: 20px; }', '    .logo { max-width: 80px; margin-bottom: 10px; }', '    .company-info { font-size: 13px; color: #6B7280; line-height: 1.5; }', '    .statement-title { text-align: right; }', '    .statement-title h1 { font-size: 28px; color: #006633; text-transform: uppercase; margin-bottom: 4px; }', '    .statement-title p { color: #6B7280; font-size: 14px; }', '    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px; }', '    .info-box { background: #f9fafb; padding: 20px; border-radius: 8px; border: 1px solid #e5e7eb; }', '    .info-box h3 { font-size: 12px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }', '    .info-box p { font-weight: 600; font-size: 16px; margin-bottom: 4px; }', '    .info-box .sub { font-weight: 400; color: #6B7280; font-size: 14px; margin-top: 2px; }', '    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 40px; }', '    .summary-item { padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; text-align: center; }', '    .summary-item .label { font-size: 11px; color: #6B7280; text-transform: uppercase; margin-bottom: 6px; }', '    .summary-item .value { font-size: 18px; font-weight: 700; }', '    table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 40px; }', '    th { background-color: #f3f4f6; color: #4b5563; font-weight: 600; text-align: left; padding: 12px; border-bottom: 2px solid #d1d5db; text-transform: uppercase; font-size: 11px; }', '    td { padding: 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }', '    .footer { text-align: center; color: #6B7280; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }', '    @media print { body { padding: 20px; } .info-box { background: transparent; border: 1px solid #000; } th { background-color: transparent; border-bottom: 2px solid #000; color: #000; } td { border-bottom: 1px solid #ccc; } * { -webkit-print-color-adjust: exact; } }', '`}</style>', '</head>', '<body>', '  <div class="header">', '    <div>', '      <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" class="logo" alt="Billvast" />', '      <div class="company-info">123 Financial District, Victoria Island<br/>Lagos, Nigeria<br/>support@billvast.com | +234 800 BILLVAST</div>', '    </div>', '    <div class="statement-title">', '      <h1>Account Statement</h1>', '      <p>Generated on ' + formatDate(now) + '</p>', '    </div>', '  </div>', '  <div class="info-grid">', '    <div class="info-box">', '      <h3>Account Details</h3>', '      <p>' + user.name + '</p>', '      <div class="sub">' + user.email + '</div>', '      <div class="sub">' + user.phone + '</div>', '    </div>', '    <div class="info-box">', '      <h3>Statement Period</h3>', '      <p>' + periodText + '</p>', '      <div class="sub">From: ' + fromDate + '</div>', '      <div class="sub">To: ' + formatDate(now) + '</div>', '    </div>', '  </div>', '  <div class="summary-grid">', '    <div class="summary-item"><div class="label">Opening Balance</div><div class="value">' + formatCurrency(openingBalance) + '</div></div>', '    <div class="summary-item"><div class="label">Total Credits</div><div class="value" style="color:#059669">+' + formatCurrency(totalCredits) + '</div></div>', '    <div class="summary-item"><div class="label">Total Debits</div><div class="value" style="color:#dc2626">-' + formatCurrency(totalDebits) + '</div></div>', '    <div class="summary-item" style="background:#f0fdf4;border-color:#bbf7d0"><div class="label">Closing Balance</div><div class="value" style="color:#059669">' + formatCurrency(user.walletBalance) + '</div></div>', '  </div>', '  <table>', '    <thead><tr><th width="15%">Date</th><th width="40%">Description</th><th width="15%" style="text-align:right">Debit</th><th width="15%" style="text-align:right">Credit</th><th width="15%" style="text-align:right">Balance</th></tr></thead>', '    <tbody>' + (rowsHtml || noRowsHtml) + '</tbody>', '  </table>', stampAndSig, '  <div class="footer">', '    This is a computer-generated statement and does not require a signature.<br/>', '    Please report any discrepancies within 14 days of receipt.', '  </div>', '  <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }<\/script>', '</body>', '</html>'].join('\n');
  win.document.write(html);
  win.document.close();
};
// --- Main Component ---
export const Wallet: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  // UI States
  const [activeTab, setActiveTab] = useState<'all' | 'in' | 'out'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  // Modal States
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState<Transaction | null>(null);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  // Form States
  const [fundAmount, setFundAmount] = useState('');
  const [fundPaymentMethod, setFundPaymentMethod] = useState<'card' | 'google_pay' | 'apple_pay' | 'mobile_pay' | 'ussd' | 'wallet'>('card');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [statementRange, setStatementRange] = useState<number>(30); // days
  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return state.transactions.filter(tx => {
      const matchesTab = activeTab === 'all' ? true : activeTab === 'in' ? tx.amount > 0 : tx.amount < 0;
      const matchesSearch = tx.title.toLowerCase().includes(searchQuery.toLowerCase()) || tx.reference.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'All' ? true : tx.type === typeFilter;
      return matchesTab && matchesSearch && matchesType;
    });
  }, [state.transactions, activeTab, searchQuery, typeFilter]);
  // Chart Data (Spending by Category)
  const spendingByCategory = useMemo(() => {
    return state.transactions.filter(tx => tx.amount < 0).reduce((acc, tx) => {
      acc[tx.type] = (acc[tx.type] || 0) + Math.abs(tx.amount);
      return acc;
    }, {} as Record<string, number>);
  }, [state.transactions]);
  const chartData = Object.entries(spendingByCategory).map(([name, value]) => ({
    name,
    value
  }));
  const totalSpent = chartData.reduce((sum, item) => sum + item.value, 0);
  // Handlers
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    const newTx: Transaction = {
      id: `tx_${Date.now()}`,
      type: 'Wallet',
      title: 'Card Funding',
      amount: amount,
      status: 'Completed',
      date: new Date().toISOString(),
      reference: `FND-${Date.now().toString().slice(-6)}`
    };
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: newTx
    });
    toast.success(`Successfully added ${formatCurrency(amount)}`);
    setIsAddFundsOpen(false);
    setFundAmount('');
    // Automatically show receipt
    setTimeout(() => setReceiptTransaction(newTx), 300);
  };
  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(withdrawAmount);
    if (amount < 1000) {
      toast.error('Minimum withdrawal is ₦1,000');
      return;
    }
    if (amount > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    if (!bankAccount) {
      toast.error('Please select a bank account');
      return;
    }
    const newTx: Transaction = {
      id: `tx_${Date.now()}`,
      type: 'Wallet',
      title: `Withdrawal to ${bankAccount.split(' - ')[0]}`,
      amount: -amount,
      status: 'Completed',
      date: new Date().toISOString(),
      reference: `WDL-${Date.now().toString().slice(-6)}`
    };
    dispatch({
      type: 'DEDUCT_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: newTx
    });
    toast.success(`Successfully withdrew ${formatCurrency(amount)}`);
    setIsWithdrawOpen(false);
    setWithdrawAmount('');
    setBankAccount('');
    // Automatically show receipt
    setTimeout(() => setReceiptTransaction(newTx), 300);
  };
  const handleGenerateStatement = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.user) return;
    const periodMap: Record<number, string> = {
      7: 'Last 7 Days',
      30: 'Last 30 Days',
      90: 'Last 3 Months',
      0: 'All Time'
    };
    generateStatementPDF(state.transactions, state.user, statementRange, periodMap[statementRange]);
    setIsStatementModalOpen(false);
    toast.success('Statement generated successfully');
  };
  // Custom Chart Label
  const renderCustomizedLabel = (props: any) => {
    const {
      cx,
      cy
    } = props.viewBox;
    return <text x={cx} y={cy} fill="#1A2E1A" textAnchor="middle" dominantBaseline="central" data-id="element-2309">
        <tspan x={cx} y={cy - 10} fontSize="13" fill="#6B7280" fontWeight="500" data-id="element-2310">
          Total Spent
        </tspan>
        <tspan x={cx} y={cy + 15} fontSize="20" fontWeight="bold" data-id="element-2311">
          {formatCurrency(totalSpent)}
        </tspan>
      </text>;
  };
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-2312">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2313">
        <div data-id="element-2314">
          <h1 className="text-3xl font-bold text-brand-text-dark tracking-tight" data-id="element-2315">
            Wallet
          </h1>
          <p className="text-brand-text-light mt-1" data-id="element-2316">
            Manage your funds, view transactions, and generate statements.
          </p>
        </div>
      </div>

      {/* Top Section: Balance & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-2317">
        {/* Modern Balance Card */}
        <div className="lg:col-span-7 relative bg-gradient-to-br from-[#004d26] via-[#006633] to-[#0A1F0A] rounded-[2rem] p-8 text-white overflow-hidden shadow-xl shadow-brand-primary-dark/20 flex flex-col justify-between min-h-[240px]" data-id="element-2318">
          {/* Abstract Mesh/Glow Effects */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-primary/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-2319" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/4 pointer-events-none" data-id="element-2320" />

          {/* Watermark Logo */}
          <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none" data-id="element-2321">
            <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="" className="w-64 h-64 grayscale" data-id="element-2322" />
          </div>

          <div className="relative z-10" data-id="element-2323">
            <div className="flex justify-between items-start mb-2" data-id="element-2324">
              <p className="text-brand-primary-light/80 font-medium tracking-wide uppercase text-sm" data-id="element-2325">
                Available Balance
              </p>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium border border-white/10 flex items-center gap-1.5" data-id="element-2326">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary-light animate-pulse" data-id="element-2327"></span>
                Active
              </div>
            </div>

            <h2 className="text-5xl sm:text-6xl font-bold tracking-tight mb-8 text-white drop-shadow-md" data-id="element-2328">
              {formatCurrency(state.user?.walletBalance || 0)}
            </h2>

            <div className="flex items-center gap-6 text-sm font-medium" data-id="element-2329">
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-2330">
                <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center" data-id="element-2331">
                  <ArrowDownRight size={14} className="text-green-300" data-id="element-2332" />
                </div>
                <div data-id="element-2333">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-2334">
                    Total In
                  </div>
                  <div className="text-white" data-id="element-2335">
                    {formatCurrency(state.transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-2336">
                <div className="w-6 h-6 rounded-full bg-red-400/20 flex items-center justify-center" data-id="element-2337">
                  <ArrowUpRight size={14} className="text-red-300" data-id="element-2338" />
                </div>
                <div data-id="element-2339">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-2340">
                    Total Out
                  </div>
                  <div className="text-white" data-id="element-2341">
                    {formatCurrency(Math.abs(state.transactions.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0)))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4" data-id="element-2342">
          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsAddFundsOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group" data-id="element-2343">
            <div className="w-14 h-14 rounded-2xl bg-green-50 text-brand-primary-dark flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors" data-id="element-2344">
              <Plus size={28} data-id="element-2345" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2346">
              Add Funds
            </span>
          </motion.button>

          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsWithdrawOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group" data-id="element-2347">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 text-gray-700 flex items-center justify-center group-hover:bg-gray-800 group-hover:text-white transition-colors" data-id="element-2348">
              <ArrowUpRight size={28} data-id="element-2349" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2350">Withdraw</span>
          </motion.button>

          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsStatementModalOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group col-span-2 sm:col-span-1 lg:col-span-2" data-id="element-2351">
            <div className="w-14 h-14 rounded-2xl bg-orange-50 text-brand-accent flex items-center justify-center group-hover:bg-brand-accent group-hover:text-white transition-colors" data-id="element-2352">
              <FileText size={28} data-id="element-2353" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2354">
              Download Statement
            </span>
          </motion.button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2355">
        {/* Transaction List */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2356">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2357">
            <h3 className="text-xl font-bold text-brand-text-dark" data-id="element-2358">
              Recent Transactions
            </h3>

            <div className="flex gap-2" data-id="element-2359">
              <div className="relative" data-id="element-2360">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-2361" />
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary w-full sm:w-48 transition-all shadow-sm" data-id="element-2362" />
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none shadow-sm" data-id="element-2363">
                <option value="All" data-id="element-2364">All Types</option>
                <option value="Wallet" data-id="element-2365">Wallet</option>
                <option value="Diaspora" data-id="element-2366">Diaspora</option>
                <option value="Bills" data-id="element-2367">Bills</option>
                <option value="Crowdfunding" data-id="element-2368">Crowdfunding</option>
                <option value="Facility" data-id="element-2369">Facility</option>
              </select>
            </div>
          </div>

          {/* Animated Tabs */}
          <div className="flex space-x-2 bg-gray-100/50 p-1 rounded-xl w-fit" data-id="element-2370">
            {(['all', 'in', 'out'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'text-brand-text-dark' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2371">
                {activeTab === tab && <motion.div layoutId="wallet-tab-indicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-2372" />}
                <span className="relative z-10" data-id="element-2373">
                  {tab === 'all' ? 'All' : tab === 'in' ? 'Money In' : 'Money Out'}
                </span>
              </button>)}
          </div>

          {/* Transaction Cards */}
          {filteredTransactions.length > 0 ? <motion.div className="space-y-3" initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.05
            }
          }
        }} data-id="element-2374">
              {filteredTransactions.map(tx => <motion.div key={tx.id} variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group" data-id="element-2375">
                  <div className="flex items-center gap-4" data-id="element-2376">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'}`} data-id="element-2377">
                      {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-2378" /> : <ArrowUpRight size={20} data-id="element-2379" />}
                    </div>
                    <div data-id="element-2380">
                      <h4 className="font-semibold text-brand-text-dark" data-id="element-2381">
                        {tx.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1" data-id="element-2382">
                        <span className="text-[11px] font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md" data-id="element-2383">
                          {tx.type}
                        </span>
                        <span className="text-xs text-brand-text-light" data-id="element-2384">
                          {formatDate(tx.date)}
                        </span>
                        <span className="text-xs text-gray-400 hidden sm:inline-block" data-id="element-2385">
                          • Ref: {tx.reference}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-6" data-id="element-2386">
                    <div className="text-right" data-id="element-2387">
                      <p className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-2388">
                        {tx.amount > 0 ? '+' : ''}
                        {formatCurrency(tx.amount)}
                      </p>
                      <div className="mt-1 flex justify-end" data-id="element-2389">
                        <StatusBadge status={tx.status} data-id="element-2390" />
                      </div>
                    </div>

                    {/* Receipt Button (Only for completed) */}
                    {tx.status.toLowerCase() === 'completed' && <button onClick={() => setReceiptTransaction(tx)} className="p-2.5 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-full transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0" title="View Receipt" data-id="element-2391">
                        <Receipt size={20} data-id="element-2392" />
                      </button>}
                  </div>
                </motion.div>)}
            </motion.div> : <div className="py-12" data-id="element-2393">
              <EmptyState icon={Search} title="No transactions found" description="Try adjusting your filters or search query." data-id="element-2394" />
            </div>}
        </div>

        {/* Spending Chart Sidebar */}
        <div className="space-y-6" data-id="element-2395">
          <h3 className="text-xl font-bold text-brand-text-dark" data-id="element-2396">Analytics</h3>
          <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 flex flex-col h-[400px]" data-id="element-2397">
            <h4 className="font-semibold text-brand-text-dark mb-6" data-id="element-2398">
              Spending by Category
            </h4>
            {chartData.length > 0 ? <div className="flex-1 relative" data-id="element-2399">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2400">
                  <PieChart data-id="element-2401">
                    <Pie data={chartData} cx="50%" cy="45%" innerRadius={70} outerRadius={90} paddingAngle={5} dataKey="value" stroke="none" data-id="element-2402">
                      {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-2403" />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                }} data-id="element-2404" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-2405" />
                  </PieChart>
                </ResponsiveContainer>
                {/* Custom Center Label Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-8" data-id="element-2406">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2407">
                    Total Spent
                  </span>
                  <span className="text-xl font-bold text-brand-text-dark mt-1" data-id="element-2408">
                    {formatCurrency(totalSpent)}
                  </span>
                </div>
              </div> : <div className="flex-1 flex items-center justify-center text-gray-400 text-sm" data-id="element-2409">
                No spending data yet
              </div>}
          </div>
        </div>
      </div>

      {/* --- Modals --- */}

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-2410">
        <form onSubmit={handleAddFunds} className="space-y-5" data-id="element-2411">
          <div data-id="element-2412">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2413">
              Amount (₦)
            </label>
            <div className="relative" data-id="element-2414">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-2415">
                ₦
              </span>
              <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold transition-all" min="1000" required data-id="element-2416" />
            </div>
            <p className="text-xs text-gray-500 mt-2" data-id="element-2417">
              Minimum amount is ₦1,000
            </p>
          </div>

          <div data-id="element-2418">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2419">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-2420">
              <button type="button" onClick={() => setFundPaymentMethod('card')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'card' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2421">
                <CreditCard size={20} data-id="element-2422" />
                <span className="text-xs font-semibold" data-id="element-2423">Card</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('ussd')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'ussd' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2424">
                <Hash size={20} data-id="element-2425" />
                <span className="text-xs font-semibold" data-id="element-2426">USSD</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('wallet')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'wallet' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2427">
                <WalletIcon size={20} data-id="element-2428" />
                <span className="text-xs font-semibold" data-id="element-2429">Wallet</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('google_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'google_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2430">
                <div className="flex items-center gap-1 font-bold text-gray-800 text-sm" data-id="element-2431">
                  <span className="text-blue-500" data-id="element-2432">G</span>Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-2433">Google Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('apple_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'apple_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2434">
                <div className="font-bold text-gray-800 text-sm flex items-center" data-id="element-2435">
                   Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-2436">Apple Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('mobile_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'mobile_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2437">
                <Smartphone size={20} data-id="element-2438" />
                <span className="text-xs font-semibold" data-id="element-2439">Mobile Pay</span>
              </button>
            </div>

            {fundPaymentMethod === 'card' ? <div className="space-y-3 animate-fade-in" data-id="element-2440">
                <div className="flex items-center gap-2 mb-2" data-id="element-2441">
                  {/* Visa */}
                  <div className="h-6 px-2 bg-blue-50 rounded flex items-center justify-center text-blue-800 font-bold italic text-xs border border-blue-100" data-id="element-2442">
                    VISA
                  </div>
                  {/* Mastercard */}
                  <div className="h-6 px-2 bg-gray-50 rounded flex items-center justify-center border border-gray-200" data-id="element-2443">
                    <div className="flex -space-x-1.5" data-id="element-2444">
                      <div className="w-3 h-3 rounded-full bg-red-500 mix-blend-multiply" data-id="element-2445"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mix-blend-multiply" data-id="element-2446"></div>
                    </div>
                  </div>
                  {/* Amex */}
                  <div className="h-6 px-2 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-[10px] tracking-tighter" data-id="element-2447">
                    AMEX
                  </div>
                  {/* Verve */}
                  <div className="h-6 px-2 bg-red-600 rounded flex items-center justify-center text-white font-bold italic text-[10px]" data-id="element-2448">
                    Verve
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm" data-id="element-2449">
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-600" data-id="element-2450">
                    <CreditCard size={20} data-id="element-2451" />
                  </div>
                  <div className="flex-1" data-id="element-2452">
                    <p className="text-sm font-semibold text-brand-text-dark" data-id="element-2453">
                      Saved Debit Card
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2454">•••• 4242</p>
                  </div>
                  <CheckCircle2 className="text-brand-primary" size={20} data-id="element-2455" />
                </div>
              </div> : fundPaymentMethod === 'ussd' ? <div className="space-y-4 animate-fade-in" data-id="element-2456">
                <div data-id="element-2457">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2458">
                    Select Bank for USSD
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none bg-white" data-id="element-2459">
                    <option value="*737#" data-id="element-2460">GTBank (*737#)</option>
                    <option value="*901#" data-id="element-2461">Access Bank (*901#)</option>
                    <option value="*770#" data-id="element-2462">Fidelity Bank (*770#)</option>
                    <option value="*894#" data-id="element-2463">First Bank (*894#)</option>
                    <option value="*822#" data-id="element-2464">Sterling Bank (*822#)</option>
                    <option value="*919#" data-id="element-2465">UBA (*919#)</option>
                    <option value="*966#" data-id="element-2466">Zenith Bank (*966#)</option>
                  </select>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center" data-id="element-2467">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-2468">
                    Dial the code below on your mobile phone to complete the
                    payment.
                  </p>
                  <div className="text-2xl font-bold text-brand-text-dark tracking-wider" data-id="element-2469">
                    *737*000*1234#
                  </div>
                </div>
              </div> : fundPaymentMethod === 'wallet' ? <div className="space-y-4 animate-fade-in" data-id="element-2470">
                <div className="bg-brand-primary-dark/5 p-4 rounded-xl border border-brand-primary/20 text-center" data-id="element-2471">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-2472">
                    Current Wallet Balance
                  </p>
                  <div className="text-2xl font-bold text-brand-primary-dark" data-id="element-2473">
                    {formatCurrency(state.user?.walletBalance || 0)}
                  </div>
                  <p className="text-xs text-brand-primary mt-2 flex items-center justify-center gap-1" data-id="element-2474">
                    <CheckCircle2 size={14} data-id="element-2475" /> Sufficient balance available
                  </p>
                </div>
              </div> : <div className="space-y-4 animate-fade-in" data-id="element-2476">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center" data-id="element-2477">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-2478">
                    {fundPaymentMethod === 'google_pay' && <span className="font-bold text-xl" data-id="element-2479">
                        <span className="text-blue-500" data-id="element-2480">G</span>Pay
                      </span>}
                    {fundPaymentMethod === 'apple_pay' && <span className="font-bold text-2xl" data-id="element-2481"></span>}
                    {fundPaymentMethod === 'mobile_pay' && <Smartphone size={32} className="text-gray-700" data-id="element-2482" />}
                  </div>
                  <h4 className="font-semibold text-brand-text-dark mb-2" data-id="element-2483">
                    Pay with{' '}
                    {fundPaymentMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2484">
                    You will be redirected to complete your{' '}
                    {fundAmount ? formatCurrency(Number(fundAmount)) : 'payment'}{' '}
                    securely.
                  </p>
                </div>
              </div>}
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-4 rounded-xl font-semibold transition-colors mt-2 shadow-md shadow-brand-primary/20" data-id="element-2485">
            Fund Wallet
          </button>
        </form>
      </Modal>

      {/* Withdraw Modal */}
      <Modal isOpen={isWithdrawOpen} onClose={() => setIsWithdrawOpen(false)} title="Withdraw Funds" data-id="element-2486">
        <form onSubmit={handleWithdraw} className="space-y-5" data-id="element-2487">
          <div className="bg-brand-primary-dark/5 p-4 rounded-xl border border-brand-primary/20 flex justify-between items-center" data-id="element-2488">
            <span className="text-sm font-medium text-brand-text-dark" data-id="element-2489">
              Available Balance
            </span>
            <span className="font-bold text-brand-primary-dark text-lg" data-id="element-2490">
              {formatCurrency(state.user?.walletBalance || 0)}
            </span>
          </div>

          <div data-id="element-2491">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2492">
              Amount (₦)
            </label>
            <div className="relative" data-id="element-2493">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-2494">
                ₦
              </span>
              <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold transition-all" min="1000" max={state.user?.walletBalance} required data-id="element-2495" />
            </div>
          </div>

          <div data-id="element-2496">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2497">
              Destination Bank
            </label>
            <select required value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white font-medium text-gray-700" data-id="element-2498">
              <option value="" disabled data-id="element-2499">
                Select saved account
              </option>
              <option value="GTBank - 0123456789" data-id="element-2500">GTBank - 0123456789</option>
              <option value="Access - 0987654321" data-id="element-2501">Access - 0987654321</option>
            </select>
          </div>

          <button type="submit" className="w-full bg-gray-900 hover:bg-black text-white py-4 rounded-xl font-semibold transition-colors mt-2 shadow-md flex items-center justify-center gap-2" data-id="element-2502">
            <Building size={18} data-id="element-2503" /> Withdraw to Bank
          </button>
        </form>
      </Modal>

      {/* Receipt Modal */}
      <Modal isOpen={!!receiptTransaction} onClose={() => setReceiptTransaction(null)} title="Transaction Receipt" maxWidth="max-w-md" data-id="element-2504">
        {receiptTransaction && <div className="space-y-6" data-id="element-2505">
            {/* Visual Receipt Card */}
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-6 relative overflow-hidden" data-id="element-2506">
              {/* Decorative top edge */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-brand-primary" data-id="element-2507"></div>

              <div className="flex justify-center mb-6 mt-2" data-id="element-2508">
                <BillvastLogo size="sm" data-id="element-2509" />
              </div>

              <div className="text-center mb-6" data-id="element-2510">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-2511">
                  Amount
                </p>
                <h3 className={`text-4xl font-bold ${receiptTransaction.amount > 0 ? 'text-brand-primary-dark' : 'text-brand-text-dark'}`} data-id="element-2512">
                  {receiptTransaction.amount > 0 ? '+' : ''}
                  {formatCurrency(receiptTransaction.amount)}
                </h3>
              </div>

              <div className="space-y-4 text-sm" data-id="element-2513">
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2514">
                  <span className="text-gray-500" data-id="element-2515">Type</span>
                  <span className="font-medium text-brand-text-dark" data-id="element-2516">
                    {receiptTransaction.type}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2517">
                  <span className="text-gray-500" data-id="element-2518">Description</span>
                  <span className="font-medium text-brand-text-dark text-right max-w-[60%]" data-id="element-2519">
                    {receiptTransaction.title}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2520">
                  <span className="text-gray-500" data-id="element-2521">Date</span>
                  <span className="font-medium text-brand-text-dark" data-id="element-2522">
                    {formatDateTime(receiptTransaction.date)}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2523">
                  <span className="text-gray-500" data-id="element-2524">Reference</span>
                  <span className="font-mono text-xs font-medium text-brand-text-dark" data-id="element-2525">
                    {receiptTransaction.reference}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1" data-id="element-2526">
                  <span className="text-gray-500" data-id="element-2527">Status</span>
                  <span className="flex items-center gap-1.5 text-green-600 font-semibold bg-green-50 px-2.5 py-1 rounded-md" data-id="element-2528">
                    <CheckCircle2 size={14} data-id="element-2529" /> {receiptTransaction.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Download Actions */}
            <div className="grid grid-cols-2 gap-3" data-id="element-2530">
              <button onClick={() => generateReceiptPNG(receiptTransaction)} className="flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2531">
                <Download size={18} data-id="element-2532" /> PNG
              </button>
              <button onClick={() => generateReceiptPDF(receiptTransaction)} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold transition-colors shadow-sm" data-id="element-2533">
                <FileText size={18} data-id="element-2534" /> PDF
              </button>
            </div>
          </div>}
      </Modal>

      {/* Statement Modal */}
      <Modal isOpen={isStatementModalOpen} onClose={() => setIsStatementModalOpen(false)} title="Generate Statement" data-id="element-2535">
        <form onSubmit={handleGenerateStatement} className="space-y-6" data-id="element-2536">
          <div className="bg-orange-50 p-4 rounded-xl flex items-start gap-3" data-id="element-2537">
            <FileText className="text-brand-accent shrink-0 mt-0.5" size={20} data-id="element-2538" />
            <p className="text-sm text-brand-text-dark" data-id="element-2539">
              Generate a formal PDF account statement. This document can be used
              for official financial verification.
            </p>
          </div>

          <div data-id="element-2540">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-2541">
              Select Date Range
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-2542">
              {[{
              label: 'Last 7 Days',
              value: 7
            }, {
              label: 'Last 30 Days',
              value: 30
            }, {
              label: 'Last 3 Months',
              value: 90
            }, {
              label: 'All Time',
              value: 0
            }].map(option => <button key={option.value} type="button" onClick={() => setStatementRange(option.value)} className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${statementRange === option.value ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`} data-id="element-2543">
                  <Calendar size={16} className={statementRange === option.value ? 'text-brand-primary' : 'text-gray-400'} data-id="element-2544" />
                  {option.label}
                </button>)}
            </div>
          </div>

          <button type="submit" className="w-full bg-brand-text-dark hover:bg-black text-white py-4 rounded-xl font-semibold transition-colors shadow-md flex items-center justify-center gap-2" data-id="element-2545">
            <Download size={18} data-id="element-2546" /> Generate PDF Statement
          </button>
        </form>
      </Modal>
    </div>;
};